import { useEffect, useId, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { getConfig } from '../lib/config';
import { useAuth } from '../lib/auth';
import { useNotifications } from '../lib/notifications';
import { useMessaging } from '../lib/messaging';
import { useOrg } from '../lib/org';
import { useSiteSettings } from '../lib/siteSettings';
import { useBranding } from '../lib/branding';
import { useSiteAccess } from '../lib/useSiteAccess';
import { ConfirmProvider } from '../lib/confirm';
import { useTheme } from '../lib/theme';
import { cn, ui } from '../lib/ui';
import { CountBadge } from './ui';
import { ToastHost } from './toast';
import AnnouncementBanner from './AnnouncementBanner';

// `badge: 'unread'` marks the one nav item that carries the live unread count.
// The nav renders once and reflows for mobile/desktop via responsive
// utilities, so the badge follows the item into both presentations. `feature`
// marks an item that FEA-30's site settings can hide (the key into
// settings.features).
const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/events', label: 'Events', feature: 'events' },
  { to: '/forum', label: 'Forum', feature: 'forum' },
  { to: '/files', label: 'Documents' },
  { to: '/videos', label: 'Videos', feature: 'videos' },
  { to: '/messages', label: 'Messages', feature: 'inbox', badge: 'messages' },
  { to: '/tasks', label: 'Tasks', feature: 'tasks' },
  { to: '/groups', label: 'Groups', feature: 'groups' },
  { to: '/schedules', label: 'Calendar', feature: 'calendar' },
  { to: '/proof-of-funds', label: 'Verification' },
  { to: '/notifications', label: 'Inbox', badge: 'unread' },
  { to: '/profile', label: 'Profile' },
  { to: '/ribbons', label: 'Ribbons' },
];

// Console zone routes (dcon design rules, "Zones"): staff and admin
// consoles get 8px controls and flat panels; everything else is the member
// zone. Add a console route's path prefix here.
const CONSOLE_PATHS = ['/review', '/admin', '/platform/admin', '/org/settings', '/org/setup'];

export function zoneForPath(pathname) {
  return CONSOLE_PATHS.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))
    ? 'console'
    : 'member';
}

const THEME_LABELS = { system: 'System', light: 'Light', dark: 'Dark' };

// Everything on the deep header uses the on-deep inks and an on-deep focus
// ring (the brand focus blue is too dark to see on Dark Blue).
const onDeepFocus = 'focus-visible:outline-dcon-on-deep';
const headerControl = cn(
  'min-h-touch cursor-pointer rounded-dcon-sm border border-dcon-on-deep-muted bg-dcon-deep px-3 py-1 text-body-s text-dcon-on-deep',
  onDeepFocus
);
const navLinkBase = cn(
  'flex min-h-touch items-center gap-2 rounded-dcon-sm px-3 py-2 text-body-s font-medium no-underline hover:bg-dcon-on-deep/15 hover:text-dcon-on-deep hover:no-underline lg:min-h-0',
  onDeepFocus
);

function ThemeSwitch() {
  const { theme, setTheme } = useTheme();
  const id = useId();
  return (
    <span className="flex items-center gap-2">
      <label htmlFor={id} className="text-body-s text-dcon-on-deep-muted">
        Theme
      </label>
      <select
        id={id}
        className={headerControl}
        value={theme}
        onChange={e => setTheme(e.target.value)}
      >
        {Object.entries(THEME_LABELS).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
    </span>
  );
}

export default function Layout() {
  const { productName, environment } = getConfig();
  const { user, logout, isReviewer } = useAuth();
  const { unreadCount } = useNotifications();
  const { unreadCount: messagesUnread } = useMessaging();
  const { orgs, activeOrg, switchOrg } = useOrg();
  const { settings } = useSiteSettings();
  const { pathname } = useLocation();
  useBranding(activeOrg?.branding);
  const [menuOpen, setMenuOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isOrgAdmin = activeOrg?.role === 'org-admin';
  const isPlatformAdmin = user?.platformRole === 'platform-admin';
  const announcement = settings.announcement || {};
  const canEditSite = useSiteAccess(activeOrg, isPlatformAdmin);

  // Dismissal is per-session and per-message: sessionStorage.setItem, keyed
  // by the announcement text, so editing the message re-shows the banner
  // even to someone who dismissed the previous one.
  const bannerKey = announcement.message
    ? `cap-announcement-dismissed:${announcement.message}`
    : null;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    setDismissed(Boolean(bannerKey && sessionStorage.getItem(bannerKey) === '1'));
  }, [bannerKey]);
  function dismissBanner() {
    if (bannerKey) sessionStorage.setItem(bannerKey, '1');
    setDismissed(true);
  }

  // Admins ALWAYS see every nav item, even ones their own feature toggle has
  // switched off, so they can't lock themselves out of judging the effect
  // (docs/FRONTEND-PLAN.md section 4.3) - the /admin route + settings form are
  // the only way to turn a feature back on, so hiding its nav item from admins
  // would be a dead end.
  const navItems = NAV_ITEMS.filter(item => {
    if (!item.feature) return true;
    if (isAdmin || isPlatformAdmin) return true;
    if (settings.features[item.feature] === false) return false;
    // A feature outside the org's plan stays in the nav (docs/Decisions.md
    // D52 item 4); its page shows the upgrade prompt.
    return true;
  });
  const fullNavItems = [
    ...navItems,
    // Investment member services (docs/Decisions.md D66).
    ...(activeOrg?.orgType === 'investment' ? [{ to: '/services', label: 'Services' }] : []),
    // Community marketplace (docs/Decisions.md D68), every organization type.
    ...(activeOrg ? [{ to: '/marketplace', label: 'Marketplace' }] : []),
    ...(isOrgAdmin ? [{ to: '/org/settings', label: 'Org settings' }] : []),
    ...(activeOrg?.canInvite ? [{ to: '/org/invite', label: 'Invite people' }] : []),
    ...(canEditSite ? [{ to: '/org/site', label: 'Website' }] : []),
    ...(isPlatformAdmin ? [{ to: '/platform/admin', label: 'Platform admin' }] : []),
    ...(isReviewer ? [{ to: '/review', label: 'Review queue' }] : []),
    ...(isAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
  ];

  return (
    <ConfirmProvider>
      <div className="flex min-h-screen flex-col">
        <a
          className="sr-only z-100 rounded-dcon-md bg-z-surface px-4 py-2 text-dcon-link shadow-dcon-md focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
          href="#main"
        >
          Skip to content
        </a>
        {!dismissed && (
          <AnnouncementBanner
            enabled={announcement.enabled}
            message={announcement.message}
            tone={announcement.tone}
            onDismiss={dismissBanner}
          />
        )}
        <header className="sticky top-0 z-50 bg-dcon-deep text-dcon-on-deep">
          <div
            className={cn(
              ui.container,
              'flex min-h-header flex-wrap items-center justify-between gap-2 py-2'
            )}
          >
            <NavLink
              to="/"
              className={cn(
                'inline-flex items-center gap-2 font-display text-title-s font-bold text-dcon-on-deep no-underline hover:text-dcon-on-deep hover:no-underline',
                onDeepFocus
              )}
            >
              {productName}
              {environment !== 'prod' && (
                <span className="rounded-dcon-pill bg-dcon-accent px-2 text-caption font-semibold uppercase text-dcon-on-accent">
                  {environment}
                </span>
              )}
            </NavLink>
            <div className="flex items-center gap-2">
              {orgs.length > 0 ? (
                <>
                  {activeOrg?.branding?.logo && (
                    <img
                      src={activeOrg.branding.logo}
                      alt=""
                      className="size-6 shrink-0 rounded-dcon-sm object-contain"
                    />
                  )}
                  <select
                    className={cn(headerControl, 'max-w-48 truncate')}
                    value={activeOrg?.orgId || ''}
                    onChange={e => switchOrg(e.target.value)}
                    aria-label="Switch organization"
                  >
                    {orgs.map(org => (
                      <option key={org.orgId} value={org.orgId}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  <NavLink
                    to="/orgs"
                    className={cn(ui.orgBrowseLink, onDeepFocus)}
                    onClick={() => setMenuOpen(false)}
                  >
                    Browse
                  </NavLink>
                </>
              ) : (
                <NavLink
                  to="/orgs"
                  className={cn(ui.orgBrowseLink, onDeepFocus)}
                  onClick={() => setMenuOpen(false)}
                >
                  Organizations
                </NavLink>
              )}
            </div>
            <button
              className={cn(headerControl, 'lg:hidden')}
              aria-expanded={menuOpen}
              aria-controls="site-nav"
              onClick={() => setMenuOpen(open => !open)}
            >
              Menu
            </button>
            <nav
              id="site-nav"
              aria-label="Main"
              className={cn(
                menuOpen ? 'flex' : 'hidden',
                'basis-full flex-col gap-1 pb-3 lg:flex lg:basis-auto lg:flex-row lg:flex-wrap lg:items-center lg:pb-0'
              )}
            >
              {fullNavItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      navLinkBase,
                      isActive ? 'bg-dcon-on-deep/15 text-dcon-on-deep' : 'text-dcon-on-deep-muted'
                    )
                  }
                >
                  {item.label}
                  {item.badge === 'unread' && (
                    <CountBadge count={unreadCount} label={`${unreadCount} unread notifications`} />
                  )}
                  {item.badge === 'messages' && (
                    <CountBadge
                      count={messagesUnread}
                      label={`${messagesUnread} unread messages`}
                    />
                  )}
                </NavLink>
              ))}
              <div className="mt-2 flex flex-wrap items-center gap-3 lg:mt-0 lg:ml-3">
                <ThemeSwitch />
                <button className={cn(ui.btnSecondary, onDeepFocus)} onClick={logout}>
                  Sign out{user ? ` (${user.firstName || user.username || user.email})` : ''}
                </button>
              </div>
            </nav>
          </div>
        </header>
        <main id="main" data-zone={zoneForPath(pathname)} className={cn(ui.page, 'bg-z-canvas')}>
          <div className={ui.container}>
            <Outlet />
          </div>
        </main>
        <footer className="mt-auto border-t border-z-line bg-z-surface py-4">
          <div className={ui.container}>
            <span className={cn(ui.muted, 'text-body-s')}>
              {productName} — private investor platform. All investment access is subject to
              verification.
              {settings.supportEmail && (
                <>
                  {' '}
                  Support: <a href={`mailto:${settings.supportEmail}`}>{settings.supportEmail}</a>
                </>
              )}
            </span>
          </div>
        </footer>
      </div>
      {/* Overlay singletons for the whole authenticated app. */}
      <ToastHost />
    </ConfirmProvider>
  );
}
